import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import {
  fetchCartItems,
  fetchPreviewCart,
  saveCart,
  updateCartSkip,
  updatePendingCart,
  updatePreviewCart,
} from './cart.api';
import { Cart } from '../../models/cart.types';

export const useCartItemsQuery = (cartId: number) => {
  const { isLoggedIn } = useAuth();
  const isLoggedOut = !isLoggedIn;
  const cartQuery = useQuery({
    queryKey: ['cartItems', cartId],
    queryFn: () => fetchCartItems(cartId!),
    enabled: isLoggedIn && !!cartId,
  });

  const previewQuery = useQuery({
    queryKey: ['previewCart'],
    queryFn: fetchPreviewCart,
    enabled: isLoggedOut,
  });

  return isLoggedIn && !!cartId ? cartQuery : previewQuery;
};

export const useSaveCartMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cart: Cart) => saveCart(cart),
    onSuccess: (savedCart: Cart) => {
      queryClient.invalidateQueries({ queryKey: ['cartItems', savedCart.id] });
      return savedCart;
    },
  });
};

export const useUpdateCartSkipMutation = () => {
  interface SkipMutationArgs {
    cartId: number,
    isSkipped: boolean,
  }

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: SkipMutationArgs) => updateCartSkip(args.cartId, args.isSkipped),
    onSuccess: (skippedCartId) => {
      queryClient.invalidateQueries({ queryKey: ['cartItems', skippedCartId] });
      queryClient.invalidateQueries({ queryKey: ['menus'] });
    },
  });
};

export const useUpdatePendingCartMutation = () => {
  const { isLoggedIn } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cart: Cart) => (isLoggedIn || cart.id
      ? updatePendingCart(cart) : updatePreviewCart(cart)),
    onSuccess: (recalculatedCart) => {
      queryClient.setQueryData(['previewCart'], recalculatedCart);
      if (recalculatedCart?.id) {
        queryClient.setQueryData(['cartItems', recalculatedCart.id], recalculatedCart);
      }
      return recalculatedCart;
    },
  });
};
